import { useState } from "react";
import { noteAttachmentLinkResponseSchema, type TimelineNoteAttachment } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { apiRequest } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Reason } from "../../design/components/Reason";
import { messageForError } from "../../i18n/errors";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";

// Gli allegati di una nota di Close (INGR-27: gli allegati come l'audio, domanda n. 82), sotto la nota nella Timeline: il nome, la
// misura e, copiato in Gioia365, «Apri» (immagini e PDF, in una scheda nuova) o «Scarica» (ogni altro tipo, che il server manda
// sempre da scaricare); non ancora copiato, lo stato: «Da copiare da Close», «Copia da Close non riuscita», «Non più in Close».
// L'indirizzo (`GET /api/note-attachments/:id/link`, valido cinque minuti) si chiede soltanto quando si preme, come «Ascolta». La
// scheda nuova si apre subito, al clic, perché il browser non la blocchi, e riceve l'indirizzo quando arriva; senza indirizzo si
// chiude e l'errore resta sotto l'allegato.

/** La misura in KB o MB, con una cifra decimale sotto i 10. */
export function attachmentSize(t: Translate, locale: Locale, bytes: number): string {
  const format = (value: number) => new Intl.NumberFormat(locale === "it" ? "it-IT" : "en-GB", { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value);
  const kb = bytes / 1024;
  if (kb < 1024) return t("timeline.note.attachments.sizeKb", { size: format(Math.max(kb, 0.1)) });
  return t("timeline.note.attachments.sizeMb", { size: format(kb / 1024) });
}

export function NoteAttachments({ attachments, tab }: { attachments: readonly TimelineNoteAttachment[]; tab: TabContext }) {
  const t = useT();
  return (
    <ul className="g-timeline-item__attachments" aria-label={t("timeline.note.attachments.title")}>
      {attachments.map((attachment) => (
        <AttachmentRow key={attachment.id} attachment={attachment} tab={tab} />
      ))}
    </ul>
  );
}

function AttachmentRow({ attachment, tab }: { attachment: TimelineNoteAttachment; tab: TabContext }) {
  const t = useT();
  const { locale } = useLocale();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function open() {
    setProblem(null);
    // Le immagini e i PDF in una scheda nuova, aperta adesso; gli altri si scaricano in questa pagina.
    const target = attachment.opensInline ? window.open("", "_blank") : null;
    if (target) target.opener = null;
    setBusy(true);
    try {
      const link = await apiRequest("GET", `/api/note-attachments/${encodeURIComponent(attachment.id)}/link`, { schema: noteAttachmentLinkResponseSchema, tab });
      if (target) {
        target.location.replace(link.url);
      } else {
        const anchor = document.createElement("a");
        anchor.href = link.url;
        anchor.rel = "noopener";
        if (!attachment.opensInline) anchor.download = attachment.filename;
        else anchor.target = "_blank";
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch (error) {
      target?.close();
      setProblem(messageForError(t, error));
    } finally {
      setBusy(false);
    }
  }

  const size = attachment.sizeBytes === null ? null : attachmentSize(t, locale, attachment.sizeBytes);
  return (
    <li className="g-timeline-item__attachment" data-state={attachment.state}>
      <span className="g-timeline-item__attachment-name">{attachment.filename}</span>
      {size === null ? null : <span className="g-timeline-item__attachment-size">{size}</span>}
      {attachment.state === "copied" ? (
        <Button
          variant="ghost"
          onClick={() => void open()}
          disabled={busy}
          aria-label={t(attachment.opensInline ? "timeline.note.attachments.openLabel" : "timeline.note.attachments.downloadLabel", { name: attachment.filename })}
        >
          {t(attachment.opensInline ? "timeline.note.attachments.open" : "timeline.note.attachments.download")}
        </Button>
      ) : (
        <span className="g-timeline-item__attachment-state">{t(`timeline.note.attachments.${attachment.state}`)}</span>
      )}
      {problem === null ? null : (
        <Reason tone="warning" role="alert">
          {problem}
        </Reason>
      )}
    </li>
  );
}
